package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/performance")
@Composable
fun ComposePerformancePage() {
    BlogLayout(
        PageProperties(
            "Compose Performance",
            ""
        ),
        BlogProperties("Jetpack Compose", "Performance")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Android Profiler, Perfetto, Firebase Performance, Macrobenchmark. Profile sessions in Android Studio, automate user journey benchmarking, field-monitor with Android Vitals.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Instruments (Xcode), MetricKit, Xcode Organizer, Firebase Performance, Instabug. On-device profiling, energy/memory/launch diagnostics, aggregate daily metrics for trends and threshold alerts.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Enable Debugging for Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    android.compose.compiler.report=true
                    android.compose.compiler.metrics=true
                    
                    androidx.benchmark.fullTracing.enable=true
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Enable stack traces at application level: onCreate
                    class SampleStackTracesEnabledApp : Application() {

                        override fun onCreate() {
                            super.onCreate()
                            // Enable Compose stack traces for minified builds only.
                            Composer.setDiagnosticStackTraceMode(ComposeStackTraceMode.Auto)

                            // Alternatively:
                            // Enable verbose Compose stack traces for local debugging
                            Composer.setDiagnosticStackTraceMode(ComposeStackTraceMode.SourceInformation)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    implementation("androidx.compose.runtime:runtime-tracing:1.10.4")
                    val composeBom = platform("androidx.compose:compose-bom:2026.02.01")
                    
                    implementation(composeBom)
                    implementation("androidx.compose.runtime:runtime-tracing")
                    
                    plugins {
                        id("com.android.library")
                        id("androidx.baselineprofile")
                    }
                    
                    ./gradlew generateBaselineProfile
                    """.trimIndent()
            )

            SpanText(
                text = "Baseline Profiles",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These are crucial for complex screens too! Pre-compile the code paths for displaying these screens. This is probably the closest to “pre-rendering” code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Asynchronous",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Fetch data in your ViewModel using coroutines. Expose it via StateFlow or SharedFlow. Load images asynchronously (Coil, Glide, Picasso). Use placeholders.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Lazy Layouts",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LazyColumn, LazyRow, LazyVerticalGrid for any list or grid. contentType if you have different types of items.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            }

            SpanText(
                text = "Phased Content Display / Progressive Rendering",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Show a basic skeleton or critical UI elements immediately.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Then, progressively load and display more complex or less critical parts. You can use state to control the visibility or composition of these sections.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect with a small delay can sometimes help ensure an initial frame is drawn before kicking off further composition, but use judiciously.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "remember, derivedStateOf, produceState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If parts of your complex screen involve heavy computations to derive UI state, cache them with remember. If state changes more often than the UI needs to update, use derivedStateOf to limit downstream recompositions. (https://multiplatformdeveloper.netlify.app/compose_side_effect)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "State Hoisting",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Keeping UI state inside composables leads to excessive recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Solution: Lift state up and manage it using ViewModel or remember.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Jetpack Compose Renders Frame",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose simplifies the what to draw part, but the responsibility for how fast it draws is still yours.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Recomposition = thinking step (state and logic re-evaluation)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Layout & Draw = painting step (turning logic into pixels)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }
        }
    }
}

//https://medium.com/androiddevelopers/faster-jetpack-compose-view-interop-with-app-startup-and-baseline-profile-8a615e061d14
//https://scribe.rip/whats-new-in-jetpack-benchmark-1-2-0-82da5092fc43
//https://proandroiddev.com/building-a-deterministic-perfetto-analyzer-for-android-95877b3e083e
//https://blog.shreyaspatil.dev/mastering-android-app-performance-analyzing-bottlenecks-with-perfetto
//https://blog.shreyaspatil.dev/exploring-pausablecomposition-internals-in-jetpack-compose